import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, PlayCircle, Save, RotateCcw, TrendingUp, TrendingDown, RefreshCw, Info } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import useSeo from "../hooks/useSeo";

/**
 * AdminPortfolio — desk-side control panel for the /portfolio simulation.
 *
 * Answers three questions the admin repeatedly asked:
 *   1. "What's actually included in the simulation right now?"
 *   2. "If I flip this alert on/off, what happens to the return?"
 *   3. "Would rotating more/less aggressively have helped?"
 *
 * The page loads /admin/backtest/alerts to get every approved alert with
 * its computed outcome + current excluded_from_stats flag. Admin toggles
 * are staged locally — the "Run preview" button hits
 * /admin/portfolio/preview to recompute the sim WITHOUT touching the
 * canonical snapshot. "Save + refresh" hits /admin/portfolio/bulk-exclusion
 * to persist the staged set and kick a snapshot refresh so the public
 * /portfolio page reflects the change.
 */
export default function AdminPortfolio() {
  useSeo({
    title: "Portfolio console — Admin — Moonbucks",
    description: "Admin control panel for the Moonbucks portfolio simulation.",
    canonical: "https://moonbucks.io/admin/portfolio",
  });

  const [windowDays, setWindowDays] = useState(90);
  const [rows, setRows] = useState(null);          // /admin/backtest/alerts rows
  const [staged, setStaged] = useState(null);      // Set<id> = current staged inclusion
  const [savedIncluded, setSavedIncluded] = useState(null); // Set<id> = the persisted state
  const [swapDelta, setSwapDelta] = useState(0.10);
  const [preview, setPreview] = useState(null);    // sim result from /admin/portfolio/preview
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load the alert list + hydrate the staged/saved sets from the DB.
  const loadAlerts = async (days = windowDays) => {
    try {
      setError(null);
      const r = await api.get(`/admin/backtest/alerts?days=${days}`);
      const rs = r.data.rows || [];
      const included = new Set(rs.filter((x) => !x.excluded_from_stats).map((x) => x.id));
      setRows(rs);
      setStaged(new Set(included));
      setSavedIncluded(new Set(included));
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load alerts");
    }
  };

  useEffect(() => { loadAlerts(windowDays); /* eslint-disable-next-line */ }, []);

  // Kick a preview when the page opens so the admin sees the current
  // published number without having to click Run first.
  useEffect(() => {
    if (staged && preview === null) runPreview();
    // eslint-disable-next-line
  }, [staged]);

  const runPreview = async () => {
    if (!staged) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/portfolio/preview", {
        window_days: windowDays,
        included_ids: Array.from(staged),
        swap_min_delta: Number(swapDelta),
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setRunning(false);
    }
  };

  const saveAndRefresh = async () => {
    if (!staged) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/portfolio/bulk-exclusion", {
        window_days: windowDays,
        included_ids: Array.from(staged),
        reason: "admin portfolio console",
      });
      toast.success(`Saved · ${r.data.included_count} kept, ${r.data.excluded_count} excluded. Snapshot refreshing.`);
      setSavedIncluded(new Set(staged));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const revertStage = () => savedIncluded && setStaged(new Set(savedIncluded));

  const toggle = (id) => {
    const next = new Set(staged);
    if (next.has(id)) next.delete(id); else next.add(id);
    setStaged(next);
  };
  const setAll = (val) => {
    if (!rows) return;
    const next = new Set(val ? rows.map((r) => r.id) : []);
    setStaged(next);
  };

  const dirty = useMemo(() => {
    if (!staged || !savedIncluded) return false;
    if (staged.size !== savedIncluded.size) return true;
    for (const x of staged) if (!savedIncluded.has(x)) return true;
    return false;
  }, [staged, savedIncluded]);

  const swaps = useMemo(
    () => (preview?.trades || []).filter((t) => t.exit_status === "SWAPPED" && t.swap_meta),
    [preview],
  );

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-mb-emerald hover:underline mb-4" data-testid="admin-portfolio-back">
          <ArrowLeft className="w-3 h-3" /> Back to admin cockpit
        </Link>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-6">
          <div>
            <div className="label-uppercase text-mb-emerald">PORTFOLIO CONSOLE</div>
            <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-1" data-testid="admin-portfolio-title">
              Real portfolio simulation — desk controls
            </h1>
            <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
              Every approved alert in the window is listed below. Tick to include, untick to exclude. "Run preview" recomputes the sim without touching the live page. "Save + refresh" commits and refreshes the public snapshot.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              className="text-sm border border-mb-border rounded-md px-2 py-1.5 bg-white"
              value={windowDays}
              onChange={(e) => { const d = parseInt(e.target.value, 10); setWindowDays(d); loadAlerts(d); setPreview(null); }}
              data-testid="admin-portfolio-window"
            >
              {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
        </div>

        {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-300 text-sm text-red-700" data-testid="admin-portfolio-error">{error}</div>}

        {/* Header stats — preview result */}
        <div className="grid sm:grid-cols-4 gap-3 mb-6" data-testid="admin-portfolio-stats">
          <StatCard label="Ending balance" value={preview ? `$${Number(preview.final_equity).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"} testid="stat-final" accent="emerald" />
          <StatCard label="Return" value={preview ? `${preview.return_pct >= 0 ? "+" : ""}${preview.return_pct.toFixed(2)}%` : "—"} testid="stat-return" accent={(preview?.return_pct ?? 0) >= 0 ? "emerald" : "red"} />
          <StatCard label={`vs SPY (${preview ? (preview.spy_return_pct >= 0 ? "+" : "") + preview.spy_return_pct + "%" : "—"})`} value={preview ? `${(preview.return_pct - (preview.spy_return_pct || 0)).toFixed(2)}pp` : "—"} testid="stat-vs-spy" accent="amber" />
          <StatCard label="Trades · Wins · Losses" value={preview ? `${(preview.trades || []).length} · ${preview.wins} · ${preview.losses}` : "—"} testid="stat-trades" />
        </div>

        {/* Controls */}
        <div className="mb-4 flex flex-wrap gap-3 items-center bg-white border border-mb-border rounded-lg p-3">
          <label className="text-xs text-neutral-500 flex items-center gap-2" data-testid="admin-portfolio-swap-delta">
            Rotation gap ≥
            <input
              type="range"
              min="0" max="0.5" step="0.02"
              value={swapDelta}
              onChange={(e) => setSwapDelta(parseFloat(e.target.value))}
              className="w-32"
            />
            <span className="font-mono-data text-mb-emerald">{swapDelta.toFixed(2)}</span>
            <span className="text-neutral-400" title="A new alert must beat the weakest open position's conviction by AT LEAST this delta before we rotate. Lower = more thrashing; higher = fewer swaps.">
              <Info className="w-3 h-3" />
            </span>
          </label>
          <div className="flex-1" />
          <button onClick={() => setAll(true)} className="text-xs text-neutral-500 hover:underline" data-testid="admin-portfolio-select-all">Select all</button>
          <button onClick={() => setAll(false)} className="text-xs text-neutral-500 hover:underline" data-testid="admin-portfolio-select-none">Select none</button>
          <button onClick={revertStage} disabled={!dirty} className="text-xs text-neutral-500 hover:underline disabled:opacity-40" data-testid="admin-portfolio-revert">
            <RotateCcw className="w-3 h-3 inline mr-1" /> Revert
          </button>
          <Button size="sm" variant="outline" onClick={runPreview} disabled={running} className="rounded-md" data-testid="admin-portfolio-run-preview">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />}
            Run preview
          </Button>
          <Button size="sm" onClick={saveAndRefresh} disabled={!dirty || saving} className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md" data-testid="admin-portfolio-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save + refresh public
          </Button>
        </div>

        {/* Alerts table */}
        <div className="bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="admin-portfolio-table-wrap">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 label-uppercase text-[10px] text-neutral-500 sticky top-0">
              <tr>
                <th className="text-center px-2 py-2 w-10">In</th>
                <th className="text-left px-2 py-2">Symbol</th>
                <th className="text-left px-2 py-2">Class</th>
                <th className="text-left px-2 py-2">Recommended</th>
                <th className="text-right px-2 py-2">Entry</th>
                <th className="text-left px-2 py-2">Outcome</th>
                <th className="text-right px-2 py-2">Return</th>
                <th className="text-right px-2 py-2">Days</th>
                <th className="text-left px-2 py-2">Exclusion note</th>
              </tr>
            </thead>
            <tbody data-testid="admin-portfolio-table">
              {(rows || []).map((r) => {
                const isIn = staged?.has(r.id);
                const ret = r.return_pct;
                return (
                  <tr key={r.id} className={`border-t border-mb-border/40 ${isIn ? "" : "bg-neutral-50/60 text-neutral-400"}`} data-testid={`admin-portfolio-row-${r.symbol}`}>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={!!isIn} onChange={() => toggle(r.id)} data-testid={`admin-portfolio-toggle-${r.symbol}`} />
                    </td>
                    <td className="px-2 py-1.5 font-mono-data font-semibold">{r.symbol}</td>
                    <td className="px-2 py-1.5 text-neutral-500">{r.alert_class}</td>
                    <td className="px-2 py-1.5 font-mono-data text-neutral-500">{r.date}</td>
                    <td className="px-2 py-1.5 font-mono-data text-right">${r.entry_price}</td>
                    <td className="px-2 py-1.5 text-neutral-700">{r.outcome || "—"}</td>
                    <td className={`px-2 py-1.5 text-right font-mono-data ${ret == null ? "text-neutral-400" : ret >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {ret == null ? "—" : `${ret >= 0 ? "+" : ""}${Number(ret).toFixed(2)}%`}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-neutral-600">{r.days_held ?? "—"}</td>
                    <td className="px-2 py-1.5 text-[11px] text-neutral-500 italic max-w-xs truncate" title={r.exclusion_reason || ""}>
                      {r.excluded_from_stats ? (r.exclusion_reason || "(no reason)") : ""}
                    </td>
                  </tr>
                );
              })}
              {rows && rows.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-neutral-400">No alerts in window.</td></tr>
              )}
              {!rows && (
                <tr><td colSpan={9} className="text-center py-6 text-neutral-400"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Rotation trace */}
        <section className="mt-8" data-testid="admin-portfolio-rotations">
          <h2 className="font-display font-semibold text-xl">Rotation trace — {swaps.length} swap{swaps.length === 1 ? "" : "s"} in preview</h2>
          <p className="text-xs text-neutral-500 mt-1">Every time the sim rotated capital, with the two conviction scores + the delta that triggered the swap.</p>
          <div className="mt-3 bg-white border border-mb-border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 label-uppercase text-[10px] text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Weak → New</th>
                  <th className="text-right px-3 py-2">Weak score</th>
                  <th className="text-right px-3 py-2">New score</th>
                  <th className="text-right px-3 py-2">Δ (threshold)</th>
                  <th className="text-right px-3 py-2">Weak P&amp;L</th>
                </tr>
              </thead>
              <tbody>
                {swaps.map((t, i) => (
                  <tr key={`${t.id}-${i}`} className="border-t border-mb-border/40" data-testid={`rotation-row-${i}`}>
                    <td className="px-3 py-1.5 font-mono-data text-neutral-500">{t.exited_on}</td>
                    <td className="px-3 py-1.5 font-mono-data">
                      <span className="text-neutral-400">{t.swap_meta.weak_symbol}</span>
                      <span className="mx-1 text-neutral-400">→</span>
                      <span className="text-mb-emerald font-bold">{t.swap_meta.new_symbol}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono-data">{t.swap_meta.weak_score}</td>
                    <td className="px-3 py-1.5 text-right font-mono-data">{t.swap_meta.new_score}</td>
                    <td className="px-3 py-1.5 text-right font-mono-data">
                      <span className={t.swap_meta.delta >= t.swap_meta.threshold ? "text-emerald-700" : "text-red-700"}>
                        {t.swap_meta.delta >= 0 ? "+" : ""}{t.swap_meta.delta.toFixed(3)}
                      </span>
                      <span className="text-neutral-400"> ({t.swap_meta.threshold.toFixed(2)})</span>
                    </td>
                    <td className={`px-3 py-1.5 text-right font-mono-data ${t.net_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {t.net_pct >= 0 ? "+" : ""}{t.net_pct.toFixed(2)}% · {t.dollars_pnl >= 0 ? "+" : ""}${t.dollars_pnl.toFixed(2)}
                    </td>
                  </tr>
                ))}
                {swaps.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-neutral-400">No swaps in this preview.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="mt-8 text-[11px] text-neutral-400">
          Preview is read-only until you click "Save + refresh public". Saving persists <span className="font-mono-data">excluded_from_stats</span> for every alert in the window and kicks a snapshot refresh so <Link to="/portfolio" className="text-mb-emerald underline">/portfolio</Link> reflects it.
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, accent, testid }) => (
  <div
    className={`bg-white rounded-lg p-4 border ${accent === "emerald" ? "border-mb-emerald" : accent === "red" ? "border-red-400" : accent === "amber" ? "border-amber-400" : "border-mb-border"}`}
    data-testid={testid}
  >
    <div className="label-uppercase text-[10px] text-neutral-500">{label}</div>
    <div className={`mt-1 font-mono-data text-2xl font-bold ${accent === "emerald" ? "text-mb-emerald" : accent === "red" ? "text-red-600" : accent === "amber" ? "text-amber-700" : "text-neutral-900"}`}>
      {value}
    </div>
  </div>
);
